import React from "react";
import Footer from "./Footer";
import { motion } from "framer-motion";

const youtubeLinks = [
  "https://youtu.be/RDCJ78KFjRY",
  "https://youtu.be/SQDOZwvy8Rs",
  "https://youtu.be/eQPKJgZRDn8",
  "https://youtu.be/C-peqQX6JOc",
  "https://youtu.be/gDGXjlhyFQ8",
  "https://youtu.be/4XT3ndLnvxA",
  "https://youtu.be/FQqalctkQWY",
  "https://youtu.be/npKfzgHOvLM",
  "https://youtu.be/q192RB4J3P8",
  "https://youtu.be/UOjmlb8S0T4",
  "https://youtu.be/HISpsoK-6sE",
  "https://youtu.be/GR4yg25halo?si=9kfCZq2HOGWOfHyg",
  "https://youtu.be/s-qM6ddLcwo?si=ql8eseZ_AuBFauOF",
];

const photoCategories = [
  {
    id: 1,
    name: "Sewing Graduation 2025",
    description: "Celebrating our sewing class graduates",
    emoji: "🎓",
    colorFrom: "from-rose-100",
    colorTo: "to-pink-50",
    border: "border-rose-200",
    badge: "bg-rose-100 text-rose-600",
  },
  {
    id: 2,
    name: "Village Renewal",
    description: "Community transformation in action",
    emoji: "🏘️",
    colorFrom: "from-emerald-100",
    colorTo: "to-green-50",
    border: "border-emerald-200",
    badge: "bg-emerald-100 text-emerald-700",
  },
  {
    id: 3,
    name: "Mental Health Programs",
    description: "Counseling & wellness initiatives",
    emoji: "💚",
    colorFrom: "from-sky-100",
    colorTo: "to-blue-50",
    border: "border-sky-200",
    badge: "bg-sky-100 text-sky-700",
  },
  {
    id: 4,
    name: "Vocational Training",
    description: "Skills development across all programs",
    emoji: "📚",
    colorFrom: "from-amber-100",
    colorTo: "to-yellow-50",
    border: "border-amber-200",
    badge: "bg-amber-100 text-amber-700",
  },
  {
    id: 5,
    name: "Community Events",
    description: "Bringing communities together",
    emoji: "🤝",
    colorFrom: "from-violet-100",
    colorTo: "to-purple-50",
    border: "border-violet-200",
    badge: "bg-violet-100 text-violet-700",
  },
  {
    id: 6,
    name: "Foundation Highlights",
    description: "Our proudest milestone moments",
    emoji: "⭐",
    colorFrom: "from-lime-100",
    colorTo: "to-green-50",
    border: "border-lime-200",
    badge: "bg-lime-100 text-lime-700",
  },
];

function VideoCard({ link, index }) {
  const videoId = link.split("https://youtu.be/")[1]?.split("?")[0];
  if (!videoId) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: index * 0.06 }}
      className="aspect-video w-full bg-gray-900 rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 ring-1 ring-black ring-opacity-5"
    >
      <iframe
        title={`video-${index}`}
        className="w-full h-full"
        src={`https://www.youtube.com/embed/${videoId}`}
        allowFullScreen
        frameBorder="0"
        loading="lazy"
      />
    </motion.div>
  );
}

function PhotoPlaceholderCard({ cat, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: index * 0.07 }}
      className={`bg-gradient-to-br ${cat.colorFrom} ${cat.colorTo} border ${cat.border} rounded-2xl p-7 flex flex-col items-center text-center shadow-sm hover:shadow-lg transition-all duration-300`}
    >
      <div className="text-5xl mb-4 select-none">{cat.emoji}</div>
      <h3 className="text-xl font-bold text-gray-800 font-dancing mb-1 leading-snug">
        {cat.name}
      </h3>
      <p className="text-gray-500 text-sm font-Inter mb-5 leading-relaxed">
        {cat.description}
      </p>
      <span
        className={`inline-flex items-center gap-1.5 text-xs font-medium font-Inter px-3 py-1.5 rounded-full ${cat.badge}`}
      >
        <svg
          className="w-3 h-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6v6l4 2M12 2a10 10 0 110 20A10 10 0 0112 2z"
          />
        </svg>
        Coming soon
      </span>
    </motion.div>
  );
}

export default function Galleries() {
  return (
    <div className="min-h-screen bg-gray-50 font-Inter">
      {/* ── Video Gallery Section ── */}
      <div className="container mx-auto px-4 pt-32 pb-16 max-w-7xl">
        <div className="text-center mb-10">
          <h2 className="text-4xl font-bold text-gray-800 font-dancing">Video Gallery</h2>
          <p className="text-gray-400 mt-2 text-sm">
            {youtubeLinks.length} videos from our programs and community events
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {youtubeLinks.map((link, index) => (
            <VideoCard key={`video-${index}`} link={link} index={index} />
          ))}
        </div>
      </div>

      {/* ── Divider ── */}
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="border-t border-gray-200" />
      </div>

      {/* ── Photo Gallery Section ── */}
      <div className="container mx-auto px-4 py-16 max-w-7xl">
        <div className="text-center mb-10">
          <h2 className="text-4xl font-bold text-gray-800 font-dancing">Photo Gallery</h2>
          <p className="text-gray-400 mt-2 text-sm">
            Our photo collection is being organized — check back soon!
          </p>
        </div>

        {/* Coming Soon Banner */}
        <div className="bg-gradient-to-r from-lime-50 to-emerald-50 border border-lime-200 rounded-2xl p-10 text-center mb-10 shadow-sm">
          <div className="text-6xl mb-4">📷</div>
          <h3 className="text-2xl font-bold text-lime-900 font-dancing mb-2">
            Photos Coming Soon
          </h3>
          <p className="text-lime-700 font-Inter max-w-sm mx-auto text-sm leading-relaxed">
            We're curating beautiful moments from our programs and community
            initiatives. Categories are ready — photos will appear here shortly.
          </p>
        </div>

        {/* Category Placeholder Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {photoCategories.map((cat, index) => (
            <PhotoPlaceholderCard key={cat.id} cat={cat} index={index} />
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
}
